import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, Package, Lightbulb, MessageCircle, HelpCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const POST_TYPES = [
  { id: 'producto', label: 'Producto', icon: Package, description: 'Comparte un producto que escaneaste' },
  { id: 'tip', label: 'Tip de góndola', icon: Lightbulb, description: 'Consejo práctico para comprar mejor' },
  { id: 'experiencia', label: 'Experiencia', icon: MessageCircle, description: 'Comparte tu experiencia' },
  { id: 'pregunta', label: 'Pregunta', icon: HelpCircle, description: 'Consulta a la comunidad' },
];

const CATEGORIES = [
  { id: 'diabetes', label: 'Diabetes' },
  { id: 'hipertension', label: 'Hipertensión' },
  { id: 'alimentacion_familiar', label: 'Alimentación Familiar' },
  { id: 'lectura_etiquetas', label: 'Lectura de Etiquetas' },
];

export default function CreatePost({ onSubmit, onClose, products = [] }) {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    post_type: '',
    product_id: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const canSubmit = formData.title && formData.content && formData.category && formData.post_type;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Nueva publicación</h2>
              <p className="text-sm text-gray-500 mt-1">Comparte con la comunidad ScanEat</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Warning */}
          <div className="p-6 bg-amber-50 border-b border-amber-100">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-900 mb-1">⚠️ Importante</p>
                <p className="text-amber-800">
                  Esta es una comunidad de apoyo, no un consultorio médico. 
                  No brindes consejos médicos ni diagnósticos. Comparte solo experiencias personales y tips prácticos.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[50vh]">
            <div className="space-y-6">
              {/* Post Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Tipo de publicación
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {POST_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = formData.post_type === type.id;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, post_type: type.id })}
                        className={`
                          p-4 rounded-xl border-2 text-left transition-all
                          ${isSelected 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                          }
                        `}
                      >
                        <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-emerald-600' : 'text-gray-400'}`} />
                        <p className={`font-medium text-sm ${isSelected ? 'text-emerald-900' : 'text-gray-900'}`}>
                          {type.label}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoría
                </label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Selecciona una categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Product (if tipo is producto) */}
              {formData.post_type === 'producto' && products.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Producto (opcional)
                  </label>
                  <Select
                    value={formData.product_id}
                    onValueChange={(value) => setFormData({ ...formData, product_id: value })}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Selecciona un producto que escaneaste" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} {product.brand && `- ${product.brand}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ej: Encontré una alternativa sin azúcar"
                  className="h-12"
                  maxLength={100}
                />
                <p className="text-xs text-gray-400 mt-1">{formData.title.length}/100</p>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contenido
                </label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Comparte tu experiencia, tip o pregunta..."
                  className="min-h-[120px] resize-none"
                  maxLength={500}
                />
                <p className="text-xs text-gray-400 mt-1">{formData.content.length}/500</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              >
                Publicar
              </Button>
            </div>

            <p className="text-xs text-gray-400 text-center mt-4">
              Tu publicación será revisada por moderadores antes de ser visible
            </p>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}